import { useMemo } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { Clock } from "lucide-react";
import { Card } from "~/components/ui/card";
import { type ViewMode } from "~/hooks/useViewMode";
import { resolveAccentPaletteColor } from "~/lib/accentPalette";
import { stripMarkdownToText } from "~/lib/stripMarkdownToText";
import { cn } from "~/lib/utils";
import { AvatarStack, AvatarStackItem } from "~/pages/manifold-nextgen/components/AvatarStack";
import { FavoriteButton } from "~/pages/manifold-nextgen/components/FavoriteButton";
import { nextgenProjectPath } from "~/pages/manifold-nextgen/paths";
import { parsePrincipal } from "~/services/principals";
import { ProjectCollaborator, ProjectListItem, projectRouteId } from "~/services/projects";
import { getOwnerDisplayName } from "~/utils/strings";
import { ProjectAccessBadge } from "./ProjectAccessBadge";
import { ProjectIcon } from "./ProjectIcon";
import { ProjectCardEnvironmentIndicator } from "./compute/ProjectCardEnvironmentIndicator";

interface ProjectCardProps {
  project: ProjectListItem;
  // Collaborators, loaded in a second phase. `undefined` while in flight.
  collaborators?: ProjectCollaborator[];
  view?: ViewMode;
  favorite?: boolean;
  // When provided, renders a favorite toggle. Omit to hide the affordance entirely.
  onToggleFavorite?: () => void;
}

// A non-favorite toggle stays hidden until the row/card is hovered or the
// button is focused; a favorited one is always visible.
const favoriteVisibility = (favorite: boolean) =>
  !favorite && "opacity-0 group-hover:opacity-100 focus-visible:opacity-100";

const MAX_CARD_AVATARS = 2;

const collaboratorName = (collaborator: ProjectCollaborator): string => {
  const { isGroup, id } = parsePrincipal(collaborator.principal);
  if (isGroup) {
    return collaborator.display_name || id;
  }
  return getOwnerDisplayName(collaborator.given_name, collaborator.family_name, id);
};

const avatarItems = (
  collaborators: ProjectCollaborator[] | undefined
): AvatarStackItem[] | undefined =>
  collaborators?.map((collaborator) => {
    const { isGroup, id } = parsePrincipal(collaborator.principal);
    return {
      id,
      name: collaboratorName(collaborator),
      imageUrl: collaborator.image_url,
      isGroup,
    };
  });

export const ProjectCard = ({
  project,
  collaborators,
  view = "card",
  favorite = false,
  onToggleFavorite,
}: ProjectCardProps) => {
  const lastActivity = formatDistanceToNowStrict(new Date(project.last_activity_at), {
    addSuffix: true,
  });
  const description = useMemo(
    () => (project.description ? stripMarkdownToText(project.description) : ""),
    [project.description]
  );

  if (view === "list") {
    return (
      <Link
        to={nextgenProjectPath(projectRouteId(project))}
        className="group relative block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div
          className={cn(
            "flex items-center gap-4 rounded-lg bg-card px-4 py-5",
            "transition-colors duration-150 group-hover:bg-muted"
          )}
        >
          <ProjectIcon project={project} size={20} />
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold text-card-foreground">{project.name}</h3>
            <p
              className={cn(
                "truncate text-xs text-muted-foreground",
                !description && "italic text-muted-foreground/60"
              )}
            >
              {description || "No description"}
            </p>
          </div>
          <div className="w-20 shrink-0">
            <ProjectAccessBadge accessLevel={project.access_level} hideDefault />
          </div>
          <div className="flex w-36 shrink-0 items-center gap-2">
            <AvatarStack items={avatarItems(collaborators)} max={MAX_CARD_AVATARS} />
            <ProjectCardEnvironmentIndicator workbenchId={project.workbench_id} />
          </div>
          <p className="flex w-36 shrink-0 items-center justify-end gap-1 text-xs text-muted-foreground">
            <Clock className="size-3.5" aria-hidden="true" />
            {lastActivity}
          </p>
          {onToggleFavorite && (
            <FavoriteButton
              favorite={favorite}
              onToggle={onToggleFavorite}
              className={cn("shrink-0", favoriteVisibility(favorite))}
            />
          )}
        </div>
      </Link>
    );
  }

  const accentColor = resolveAccentPaletteColor(project.accent_color ?? undefined);

  return (
    <Link
      to={nextgenProjectPath(projectRouteId(project))}
      className="group relative block h-full rounded-lg transition-transform duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Card
        className={cn(
          "relative flex h-full min-h-80 gap-0 overflow-hidden rounded-l-lg p-0",
          "shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
        )}
      >
        <div
          className="absolute inset-y-0 left-0 w-10 overflow-hidden rounded-l-lg"
          style={{ backgroundColor: accentColor.bg }}
          aria-hidden="true"
        />
        {onToggleFavorite && (
          <FavoriteButton
            favorite={favorite}
            onToggle={onToggleFavorite}
            className={cn("absolute right-2.5 top-2.5 z-20", favoriteVisibility(favorite))}
          />
        )}
        <div className="ml-10 flex flex-1 flex-col justify-between gap-3">
          <div className="flex flex-col gap-3 px-5 pt-5 pb-4">
            <h3 className="truncate pr-7 text-base font-semibold leading-tight text-card-foreground group-hover:text-foreground">
              {project.name}
            </h3>

            <p
              className={cn(
                "line-clamp-2 min-h-[2.5rem] text-sm text-muted-foreground",
                !description && "italic text-muted-foreground/60"
              )}
            >
              {description || "No description"}
            </p>
          </div>

          <div className="flex flex-col gap-2 px-5 py-2.5">
            <ProjectCardEnvironmentIndicator workbenchId={project.workbench_id} />
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Shared with</span>
              <AvatarStack items={avatarItems(collaborators)} max={MAX_CARD_AVATARS} />
            </div>
            <div className="flex items-center gap-3">
              <ProjectAccessBadge accessLevel={project.access_level} hideDefault />
              <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="size-3.5" aria-hidden="true" />
                {lastActivity}
              </span>
            </div>
          </div>
        </div>
      </Card>

      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 rounded-xl",
          "opacity-0 shadow-[0_4px_10px_rgba(15,23,42,0.06)]",
          "transition-opacity duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] group-hover:opacity-100"
        )}
      />
    </Link>
  );
};
